import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useComlinkWorker, createComlinkWorkerFactory } from "hooks/useComlinkWorker";
import { setIsModelsLoaded, setModelMetadata, setPipelineVersion } from "store/slices/imageRecognition";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { setTensorflowWorker, getModelMetaDataTags } from "services/models";
import { WORKER_CONFIG } from '../constants/worker';
import { isWorkerMethodError, retryWithBackoff, sleep } from '../utils/workerHelpers';
import { getTestTypeValue } from 'store/slices/test';
import { MODEL_URLS_BY_TEST_TYPE, VERSION_URLS } from 'constants/models';
import type { LoadModelsResult, WorkerApiInterface } from 'types/models';
import { sentryLogMessage, sentryLogException } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";

const workerFactory = createComlinkWorkerFactory(() => import("../workers/tensorflow.worker.ts?worker"));

/**
 * Hook for loading and managing TensorFlow models in a web worker
 */
const useModels = () => {
  const testType = useAppSelector(getTestTypeValue);
  const [isDoneLoadingModels, setIsDoneLoadingModels] = useState(false);
  const hasStartedLoadingRef = useRef(false);
  const isLoadingModelsRef = useRef(false);
  const lastWorkerRef = useRef<unknown>(null);
  const dispatch = useAppDispatch();
  const worker = useComlinkWorker<WorkerApiInterface>(workerFactory);
  const {localizationUrl, classificationUrl} = useMemo(() =>  MODEL_URLS_BY_TEST_TYPE[testType], [testType]);

  // Set worker in services/models when available; null it out on unmount
  useEffect(() => {
    if (worker) {
      setTensorflowWorker(worker);
    }
    return () => {
      // [M1-FIX] Null the worker reference on unmount so models.ts doesn't
      // hold a stale reference to a terminated Comlink proxy
      setTensorflowWorker(null);
    };
  }, [worker]);

  /**
   * Load models into the worker
   */
  const loadModels = useCallback(async () => {
    if (!worker) {
      console.warn('Models: Worker not ready');
      return;
    }
    if (isLoadingModelsRef.current || isDoneLoadingModels) {
      return;
    }

    isLoadingModelsRef.current = true;

    try {
      const result = await retryWithBackoff<LoadModelsResult>(
        async () => {
          const response = await worker.loadModel(
            localizationUrl,
            classificationUrl,
            VERSION_URLS.localizationVersionUrl,
            VERSION_URLS.classificationVersionUrl,
          );
          if (!response?.success) {
            throw new Error('Model loading returned unsuccessful result');
          }
          return response;
        },
        WORKER_CONFIG.MAX_LOAD_RETRIES,
        WORKER_CONFIG.METHOD_CALL_DELAY,
        (attempt, error) => {
          if (isWorkerMethodError(error)) {
            console.warn(`Models: Worker method not available (attempt ${attempt}), retrying...`);
          }
        }
      );

      if (result.success) {
        if (result.metadata) {
          dispatch(setModelMetadata(result.metadata));
          const pipelineVersion = await worker.pipeline_version();
          dispatch(setPipelineVersion( pipelineVersion ))
          sentryLogMessage({
            message: 'Models metadata retrieved',
            level: 'info',
            tags: {
              component: 'useModels',
              category: 'modelLoad',
              event_type: SENTRY_EVENT_TYPES.MODEL_LOADED,
              ...getModelMetaDataTags(),
            },
            extra: {
              localizationUrl,
              classificationUrl,
              metadata: result.metadata,
              timestamp: new Date().toISOString(),
            },
          });
        }
        dispatch(setIsModelsLoaded(true));
        setIsDoneLoadingModels(true);

        sentryLogMessage({
          message: 'Models loaded successfully',
          level: 'info',
          tags: {
            component: 'useModels',
            category: 'modelLoad',
            event_type: SENTRY_EVENT_TYPES.MODEL_LOADED,
            ...getModelMetaDataTags(),
          },
          extra: {
            localizationUrl,
            classificationUrl,
            metadata: result.metadata,
            timestamp: new Date().toISOString(),
          },
        });
      }
    } catch (error) {
      console.error('Models: Failed to load models after retries:', error);
      
      // Log error to Sentry
      sentryLogException({
        error,
        tags: {
          testType,
          component: 'useModels',
          category: 'modelLoad',
          errorType: 'model_loading_failed',
          ...getModelMetaDataTags(),
        },
        extra: {
          localizationUrl,
          classificationUrl,
          maxRetries: WORKER_CONFIG.MAX_LOAD_RETRIES,
        },
      });
      
      // Set as loaded to prevent infinite loading state
      dispatch(setIsModelsLoaded(true));
    } finally {
      isLoadingModelsRef.current = false;
    }
  }, [worker, dispatch, localizationUrl, classificationUrl, isDoneLoadingModels]);

  useEffect(() => {
    if (worker && worker !== lastWorkerRef.current) {
      lastWorkerRef.current = worker;
      hasStartedLoadingRef.current = false;
      isLoadingModelsRef.current = false;
    }
    if (worker && !hasStartedLoadingRef.current) {
      hasStartedLoadingRef.current = true;
      // Small delay to ensure worker is fully initialized
      sleep(WORKER_CONFIG.INITIALIZATION_DELAY).then(() => {
        loadModels();
      });
    }
  }, [worker, loadModels]);

  return {
    isDoneLoadingModels,
  };
};

export default useModels;